import type { TRPCClientErrorLike } from "@trpc/client";
import type { InferrableClientTypes } from "@trpc/server/unstable-core-do-not-import";
import type {
  FieldErrors,
  FieldValues,
  Path,
  UseFormSetError,
} from "react-hook-form";

import { handleTRPCClientError } from "@v2/utils/error-handling";
import { isEmpty } from "replo-utils/lib/misc";
import { z } from "zod";

/**
 * Creates a standardized error handler for tRPC mutations with react-hook-form.
 *
 * This handler automatically:
 * - Parses flattened Zod errors from the backend (error.data)
 * - Maps backend field names to form field names
 * - Displays field-specific errors using setError()
 * - Falls back to handleTRPCClientError() for non-Zod errors (500s, etc.)
 *
 * @example
 * ```typescript
 * const { setError } = useForm<FormData>();
 *
 const { mutate } = trpc.workspace.inviteMembers.useMutation({
   onSuccess: () => { ... },
   ...createFormErrorHandler(setError, {
     // Map backend field names to form field names
     usersToGrantAccess: "emails",
   }),
 });
 * ```
 *
 * @param setError - The setError function from react-hook-form's useForm
 * @param fieldMapping - Optional mapping of backend field names to form field names. If a backend field name is not in this map, it will be used as-is.
 * @returns An object with onError and meta properties to spread into useMutation options
 */
export function createFormErrorHandler<
  FieldValuesType extends FieldValues,
  TRPCClientTypes extends InferrableClientTypes,
>(
  setError: UseFormSetError<FieldValuesType>,
  fieldMapping: Partial<Record<string, Path<FieldValuesType>>> = {},
) {
  return {
    onError: (error: TRPCClientErrorLike<TRPCClientTypes>) => {
      // Parse the flattened Zod error structure from backend
      const zodFormErrorData = z
        .object({
          fieldErrors: z.record(z.string(), z.array(z.string())).nullish(),
          formErrors: z.array(z.string()).nullish(),
        })
        .safeParse(error.data);

      // If we successfully parsed Zod errors and there's data, display them
      if (zodFormErrorData.success && !isEmpty(zodFormErrorData.data)) {
        const { fieldErrors, formErrors } = zodFormErrorData.data;

        if (fieldErrors) {
          for (const [backendFieldName, errorMessages] of Object.entries(
            fieldErrors,
          )) {
            if (errorMessages && errorMessages.length > 0) {
              const formFieldName =
                (fieldMapping[backendFieldName] as Path<FieldValuesType>) ??
                (backendFieldName as Path<FieldValuesType>);

              setError(formFieldName, {
                message: errorMessages[0],
              });
            }
          }
        }

        if (formErrors && formErrors.length > 0) {
          setError("root" as Path<FieldValuesType>, {
            message: formErrors.join(". "),
          });
        }
      } else {
        // Not a Zod error - fall back to standard error handling
        // This handles 500s, network errors, ReploHTTPErrors, etc.
        handleTRPCClientError(error);
      }
    },
    meta: {
      reploIgnoreDefaultErrorHandling: true,
    },
  };
}

/**
 * Given a react-hook-form errors object, return the first applicable error
 * message.
 *
 * Useful for things like displaying a tooltip over a disabled form submit
 * button to tell the user why they can't submit the form.
 */
export function getFirstError<FieldValuesType extends FieldValues>(
  errors: FieldErrors<FieldValuesType>,
) {
  const firstError = Object.values(errors)[0];
  if (Array.isArray(firstError)) {
    return firstError[0]?.message as string | undefined;
  }
  return firstError?.message as string | undefined;
}
